import type { Concept } from '@yuan-math/curriculum';

/**
 * Algebra II — the band where the pieces stop being separate topics.
 *
 * Algebra I made an *expression* into an object with a shape you could change.
 * Geometry made an *argument* into one. This course does the same for the
 * function: something you can shift, stretch, invert and compose, and which
 * keeps its identity through all of it. Nearly every lesson here is the same
 * sentence said about a different curve — which is exactly the point, and why
 * `function-transformations` sits so early and so many things depend on it.
 *
 * The other half of the course completes the number system. A student leaving
 * Algebra I believes x² + 1 = 0 has no solutions, which is true only of the
 * numbers they have been given so far. Admitting i is not a trick to make an
 * awkward equation go away: it is what makes "a polynomial of degree n has n
 * roots" true as stated, with no exceptions to memorise.
 *
 * Three orderings deserve their comments, and all three are pinned by tests.
 *
 * `polynomial-functions` depends on `complex-numbers`, because the counting
 * theorem it is built on is false until the roots are allowed to be complex.
 *
 * `logarithms` depends on `inverse-functions` rather than only on
 * `exponential-functions`. A logarithm *is* the inverse of an exponential; a
 * student who has not met the idea of undoing a function meets the three log
 * laws as three unmotivated identities, and forgets them in that form.
 *
 * `trig-graphs` depends on `function-transformations` as well as on
 * `unit-circle`. Amplitude and period are a vertical and a horizontal stretch —
 * the same idea already met on parabolas, not a new one wearing a hat.
 *
 * Three more were added when the year was completed.
 *
 * `radical-equations` depends on `rational-exponents`. "Square both sides" is
 * a rule; "raise both sides to the power that undoes the exponent ½" is the
 * reason, and it is the same reason for a cube root and for a fourth root.
 *
 * `inverse-functions` depends on `function-operations`. Composition is not
 * decoration there: f(g(x)) = x *is* the definition of an inverse, and a
 * student who cannot compose can only check an inverse by drawing it.
 *
 * `polynomial-inequalities` depends on `polynomial-functions` rather than on
 * `factoring` alone, because the method is to read a graph's sign — where the
 * curve is above the axis — and not to manipulate the inequality symbol.
 *
 * Seven more arrived when the year was checked against the Grade 11 syllabus,
 * and three of their orderings are the interesting ones.
 *
 * `tangent-function` depends on `rational-functions`, not merely on
 * `unit-circle`. Tangent is sine over cosine, so its asymptotes are a
 * denominator hitting zero — the same sentence already said about 1/(x − 2),
 * and a student who has met it once does not have to be told twice that a
 * curve can approach a line it never reaches.
 *
 * `natural-logarithm` depends on `exponential-models` rather than only on
 * `logarithms`. e is not a constant to be handed over; it is what compound
 * interest converges to as the compounding period shrinks, and that limit has
 * to have been watched before ln is anything other than a button.
 *
 * `normal-distribution` depends on `histograms` as well as on
 * `standard-deviation`. The bell curve is the shape a histogram approaches as
 * the bars get narrower and the sample grows, and presented without that it is
 * a picture with three percentages written under it.
 */
export const ALGEBRA_TWO_CONCEPTS: Concept[] = [
  /* ------------------------------------------ completing the number system */
  {
    id: 'rational-exponents',
    title: 'Rational Exponents',
    summary: 'What x to the power of one half must mean, once you insist the exponent rules keep working.',
    strand: 'numbers',
    levels: ['grade-11'],
    prerequisites: ['radicals', 'exponent-rules'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'complex-numbers',
    title: 'Complex Numbers',
    summary: 'Admitting a square root of −1, and finding that it settles every quadratic at once.',
    strand: 'numbers',
    levels: ['grade-11'],
    prerequisites: ['radicals', 'quadratic-formula'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------------ polynomials, up */
  {
    id: 'polynomial-division',
    title: 'Dividing Polynomials',
    summary: 'Long division for expressions — and the shortcut that turns a division into a single substitution.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['polynomials', 'factoring'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'polynomial-functions',
    title: 'Polynomial Functions',
    summary: 'What a curve of degree three or more does at its ends, at its roots, and in between.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['polynomial-division', 'quadratic-graphs', 'complex-numbers'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'polynomial-inequalities',
    title: 'Polynomial and Rational Inequalities',
    summary: 'When the answer is not a number but a stretch of the number line — and how a graph hands it to you.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['polynomial-functions', 'inequalities'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* ------------------------------------------------- functions as objects */
  {
    id: 'function-operations',
    title: 'Combining and Composing Functions',
    summary: 'Adding, multiplying and — the one that is genuinely new — feeding one function into another.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['functions-intro', 'polynomials'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'function-transformations',
    title: 'Transformations of Functions',
    summary: 'Shifting, stretching and reflecting any curve at all — one set of rules for every function you will meet.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['quadratic-graphs', 'functions-intro'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'inverse-functions',
    title: 'Inverse Functions',
    summary: 'Running a function backwards — when that is possible, and what it does to the graph.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['function-transformations', 'linear-functions', 'function-operations'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'piecewise-functions',
    title: 'Piecewise Functions',
    summary: 'One function with more than one rule, and the endpoint that decides which rule owns the join.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['functions-intro', 'absolute-value'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------- where the domain bites back */
  {
    id: 'rational-functions',
    title: 'Rational Functions',
    summary: 'Curves with a forbidden input, and the lines they approach but never reach.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['rational-expressions', 'polynomial-functions', 'domain-and-range'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'rational-equations',
    title: 'Solving Rational Equations',
    summary: 'Clearing the denominators, and the answer that has to be thrown away because it was never allowed.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['rational-expressions', 'quadratics'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'radical-equations',
    title: 'Radical Equations',
    summary: 'Squaring both sides solves the equation and invents answers — so checking stops being optional.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['radicals', 'quadratics', 'rational-exponents'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* --------------------------------------------- two curves, meeting or not */
  {
    id: 'nonlinear-systems',
    title: 'Systems With a Curve',
    summary: 'Two equations where one of them bends — so the answer is two points, or one, or none at all.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['systems-of-equations', 'quadratic-graphs'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* -------------------------------------------------------------- undoing */
  {
    id: 'logarithms',
    title: 'Logarithms',
    summary: 'The question "what power was that?" — asked often enough to deserve its own function.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['exponential-functions', 'inverse-functions'],
    status: 'available',
    estimatedMinutes: 40,
  },

  {
    id: 'exponential-equations',
    title: 'Exponential and Logarithmic Equations',
    summary: 'Getting the unknown down out of the exponent — the job logarithms were invented to do.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['logarithms', 'exponential-functions'],
    status: 'available',
    estimatedMinutes: 40,
  },

  /* --------------------------------------------------- trigonometry, freed */
  {
    id: 'unit-circle',
    title: 'The Unit Circle',
    summary: 'Freeing sine and cosine from the right triangle, so that every angle has them.',
    strand: 'trigonometry',
    levels: ['grade-11'],
    prerequisites: ['trig-ratios', 'coordinate-plane'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'trig-graphs',
    title: 'Graphs of Sine and Cosine',
    summary: 'What a circle looks like when you unroll it — its height, its length, where it starts, and why so much of the world is shaped like this.',
    strand: 'trigonometry',
    levels: ['grade-11'],
    prerequisites: ['unit-circle', 'function-transformations'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'tangent-function',
    title: 'The Tangent Function',
    summary: 'The third ratio, which is a quotient — so it has gaps where sine and cosine have none.',
    strand: 'trigonometry',
    levels: ['grade-11'],
    prerequisites: ['unit-circle', 'rational-functions'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------------ the shapes of growth */
  {
    id: 'radical-functions',
    title: 'Radical Functions',
    summary: 'The square-root curve and the cube-root curve — and why only one of them has a left-hand half.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['rational-exponents', 'function-transformations'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'exponential-models',
    title: 'Exponential Models',
    summary: 'Compound interest, populations and half-lives — and the number that appears when compounding never stops.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['exponential-functions', 'percent-applications'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'natural-logarithm',
    title: 'Common and Natural Logarithms',
    summary: 'The two bases that get their own buttons, and why one of them is not a round number.',
    strand: 'functions',
    levels: ['grade-11'],
    prerequisites: ['logarithms', 'exponential-models'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'recursive-sequences',
    title: 'Recursive Sequences',
    summary: 'A rule that defines each term from the one before — and what it costs you to have written it that way.',
    strand: 'algebra',
    levels: ['grade-11'],
    prerequisites: ['sequences'],
    status: 'available',
    estimatedMinutes: 35,
  },

  /* ------------------------------------------- statistics, made continuous */
  {
    id: 'normal-distribution',
    title: 'Normal Distributions',
    summary: 'The bell-shaped curve so many measurements fall into, and how a standard deviation reads a position on it.',
    strand: 'statistics',
    levels: ['grade-11'],
    prerequisites: ['standard-deviation', 'histograms'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'regression',
    title: 'Regression and Correlation',
    summary: 'Fitting a line to a cloud of points, measuring how well it fits, and knowing where the line stops meaning anything.',
    strand: 'statistics',
    levels: ['grade-11'],
    prerequisites: ['scatter-plots', 'linear-equation-forms'],
    status: 'available',
    estimatedMinutes: 40,
  },
];
